import baustein from "baustein";
import _ from "underscore";
import rothko from "web/script/modules/rothko";
import analytics from "web/script/analytics/analytics";
import globals from "web/script/modules/globals";
import userProfiler from "web/script/modules/userprofiler";
import "web/script/components/save-for-later-button";
import "web/script/components/save-for-later-signup-prompt";
import "web/script/components/tracked-link";

const CLOSE_CROSS_SELECTOR = ".cart-summary__close-button";
const CHECKOUT_BUTTON_SELECTOR = ".cart-summary__cta--checkout";

export default baustein.register("cart-summary", {
    template: () =>
        import(
            /* webpackChunkName: "template-cart-summary" */
            "templates/modules/cart_summary.jinja"
        ),

    defaultOptions: {
        show_close_cross: false,
        productAnalyticsData: null,
        checkStock: false,
    },

    init() {
        this.cartSummaryClient = new rothko.Rothko("modules/cart_summary", {
            cache: false,
        });

        this.cartSummaryClient.on("data", (event, data) => {
            this.updateOptions({ productAnalyticsData: data.product_analytics_data });

            if (data.state == "oos_notification") {
                if (data.item && !userProfiler.hasSeenOOSNotification(data.item.product_id)) {
                    // tell parent to persist display of this component
                    this.emit("oosItemFound");
                    userProfiler.saveSeenOOSNotification(data.item.product_id);
                } else {
                    // don't render
                    return;
                }
            }

            this.setRenderContext(this.makeContext(data));
        });

        if (this.options.checkStock) {
            this.checkStock();
        } else {
            this.update();
        }
    },

    setupEvents(add) {
        add("click", CLOSE_CROSS_SELECTOR, this.close);
        add("click", (event) => event.stopPropagation());
        add("click", CHECKOUT_BUTTON_SELECTOR, this._onClickCheckoutButton);
    },

    /**
     * On options change, always re-render.
     */
    onOptionsChange() {
        const context = this.getRenderContext();
        this.setRenderContext(context);
    },

    /**
     * Get the initial render context.
     *
     * We want the initial state to be 'loading'
     */
    getInitialRenderContext() {
        return this.makeContext({ state: "loading" });
    },

    /**
     * Returns the context to render the component.
     *
     * We want the component option 'show_close_cross' to over-write the context provided
     * by the server since only the component can really know whether to show the close
     * cross or not.
     *
     * @param {Object} context The context from the server.
     */
    makeContext(context) {
        return {
            cart_summary: _.extend(context, { show_close_cross: this.options.show_close_cross }),
        };
    },

    /**
     * Shows the close cross
     */
    showCloseCross() {
        this.options.show_close_cross = true;
    },

    /**
     * Event handler for clicks on the close cross.
     */
    close() {
        this.emit("closeIntent");
        this.options.show_close_cross = false;
    },

    /**
     * Fetch new data and re-render.
     */
    update() {
        this.cartSummaryClient.fetch().catch(() => {});
    },

    /**
     * Like update(), but specifically for a stock check
     */
    checkStock() {
        this.cartSummaryClient.fetch({ check_in_stock: true }).catch(() => {});
    },

    _onClickCheckoutButton(event) {
        event.preventDefault();

        let href = event.target.href;

        (this.options.productAnalyticsData || []).forEach((product) => {
            analytics.ecommerce("ec:addProduct", {
                name: product.product_canonical_slug,
                category: product.category,
                brand: product.designer_slug,
                variant: product.product_uid,
                price: product.price,
                quantity: 1,
                dimension20: product.gender,
                metric8: product.sale_discount,
            });
        });

        analytics.ecommerce("ec:setAction", "checkout", {
            step: 1,
        });

        analytics.event("CART_SUMMARY", "CHECKOUT").then(() => {
            globals.window.location = href;
        });
    },
});
